import { apiClient } from './client';
import { AuthUser } from '@/types';
import { getDeviceCredentialId } from '@/lib/deviceAuth';

export interface LoginPayload {
  emailOrUsername: string;
  password: string;
  /** 6-digit TOTP code. Required when the account has 2FA enabled — the server
   *  answers a first attempt without it with 401 code TOTP_CODE_REQUIRED. */
  totpCode?: string;
}

export interface RegisterPayload {
  email: string;
  username: string;
  displayName: string;
  password: string;
  tosAccepted: boolean;
  /**
   * Cloudflare Turnstile token. Backend `verifyTurnstile` middleware (на
   * /api/auth/register, /forgot-password, /resend-verification) проверяет его
   * через CF siteverify. Если TURNSTILE_SECRET_KEY не задан на бэке — поле
   * игнорируется (graceful disable для dev/staging).
   */
  cfTurnstileToken: string;
}

export interface AuthResponse {
  accessToken: string;
  user: AuthUser;
}

export const authApi = {
  async register(data: RegisterPayload) {
    const res = await apiClient.post<{ message: string; user: Pick<AuthUser, 'id' | 'username'> }>(
      '/api/auth/register',
      data,
    );
    return res.data;
  },

  async login(data: LoginPayload): Promise<AuthResponse> {
    const res = await apiClient.post<AuthResponse>('/api/auth/login', data);
    return res.data;
  },

  async logout() {
    await apiClient.post('/api/auth/logout');
  },

  async refresh(): Promise<{ accessToken: string }> {
    const res = await apiClient.post<{ accessToken: string }>('/api/auth/refresh');
    return res.data;
  },

  async confirmEmail(token: string) {
    const res = await apiClient.get<{ message: string }>(`/api/auth/confirm/${token}`);
    return res.data;
  },

  async forgotPassword(email: string, cfTurnstileToken: string) {
    const res = await apiClient.post<{ message: string }>('/api/auth/forgot-password', {
      email,
      cfTurnstileToken,
    });
    return res.data;
  },

  async resetPassword(token: string, password: string) {
    const res = await apiClient.post<{ message: string }>('/api/auth/reset-password', { token, password });
    return res.data;
  },

  // ── v5.3 Self-service account restore (152-ФЗ + GDPR Art 17 reversibility) ─
  async requestAccountRestore(emailOrUsername: string, password: string) {
    const res = await apiClient.post<{ message: string }>(
      '/api/auth/account/restore-request',
      { emailOrUsername, password },
    );
    return res.data;
  },

  async confirmAccountRestore(token: string) {
    const res = await apiClient.post<{ message: string; username: string }>(
      '/api/auth/account/restore',
      { token },
    );
    return res.data;
  },

  async me(): Promise<AuthUser> {
    const res = await apiClient.get<AuthUser>('/api/auth/me');
    return res.data;
  },

  async getSessions(): Promise<Array<{
    id: string;
    ipAddress: string | null;
    userAgent: string | null;
    createdAt: string;
    expiresAt: string;
    isCurrent: boolean;
  }>> {
    const res = await apiClient.get('/api/auth/sessions');
    return res.data;
  },

  async revokeSession(id: string): Promise<void> {
    await apiClient.delete(`/api/auth/sessions/${id}`);
  },

  async revokeAllSessions(): Promise<void> {
    // Передаём id своего устройства, чтобы «выйти со всех устройств» оставил
    // ЭТОТ браузер залогиненным (ревокнул device-credential'ы всех остальных).
    // Без id сервер ревокнул бы и текущее устройство тоже.
    const currentDeviceId = await getDeviceCredentialId();
    await apiClient.delete('/api/auth/sessions', {
      data: currentDeviceId ? { currentDeviceId } : undefined,
    });
  },

  // ── v5.0 Privacy toggles (152-ФЗ minimization + GDPR Art 7 control) ──────
  async getPrivacy(): Promise<PrivacySettings> {
    const res = await apiClient.get<PrivacySettings>('/api/users/privacy');
    return res.data;
  },

  async patchPrivacy(patch: Partial<PrivacySettings>): Promise<PrivacySettings> {
    const res = await apiClient.patch<PrivacySettings>('/api/users/privacy', patch);
    return res.data;
  },

  // ── v5.0 Account self-service (152-ФЗ ст. 14 / GDPR Art 17 + 20) ─────────

  /**
   * Soft-delete the current account. The server demands a fresh password
   * verification — bearer-token-only is rejected. After success the server
   * has already revoked all refresh tokens; UI must logout locally.
   */
  async deleteAccount(password: string, reason?: string): Promise<{
    message: string;
    deletedAt: string;
    scheduledPurgeAt: string;
    graceDays: number;
  }> {
    const res = await apiClient.post('/api/auth/account/delete', { password, reason });
    return res.data;
  },

  /**
   * Stream the user's full data dump as a JSON file. We let the browser
   * handle the actual download via a hidden anchor — never load the body
   * into a JS string because exports for active users are 50–200 MB.
   */
  async exportAccount(): Promise<void> {
    const res = await apiClient.get('/api/auth/account/export', { responseType: 'blob' });
    // Filename comes from the Content-Disposition header; fall back to a
    // deterministic name so this still works on browsers that strip the
    // header (cross-origin proxy, etc).
    let filename = `swipa-export-${Date.now()}.json`;
    const cd = (res.headers as Record<string, string>)?.['content-disposition'];
    if (cd) {
      const m = cd.match(/filename="([^"]+)"/) ?? cd.match(/filename=([^;]+)/);
      if (m) filename = m[1].trim();
    }
    const blob = res.data instanceof Blob ? res.data : new Blob([res.data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  },
};

// ── v5.1 Privacy types ─────────────────────────────────────────────────────
// Backend mirror of Prisma `PrivacyAudience` enum. Keep in sync with
// schema.prisma. Used for whoCanCallMe / whoCanAddMeToChannels.
export type PrivacyAudience = 'EVERYONE' | 'FRIENDS' | 'NOBODY';

export interface PrivacySettings {
  hideLastSeen: boolean;
  hideOnline: boolean;
  hideReadReceipts: boolean;
  whoCanCallMe: PrivacyAudience;
  whoCanAddMeToChannels: PrivacyAudience;
}

export const PRIVACY_AUDIENCE_LABELS: Record<PrivacyAudience, string> = {
  EVERYONE: 'Все',
  FRIENDS: 'Только друзья',
  NOBODY: 'Никто',
};
